import { PeekBox } from '@piiqtechnologies/ui-components';
import React, { createContext, useContext } from 'react';

const PolicyContext = createContext(null);
/**
 * @description PolicyContextProvider provides the application policies to the rest of the components, it enables the use of `<Policy />` and `usePolicyContext`
 */
const PolicyContextProvider = ({ children, allow, deny }) => {
    return React.createElement(PolicyContext.Provider, { value: { allow, deny } }, children);
};
const usePolicyContextAux = () => {
    return useContext(PolicyContext);
};
/**
 * This component is used to hide or disable a component based on the provided policy.
 * The current user's policies are provided by the PolicyContextProvider.
 *
 * @param mode - "hide" or "disable" the component. If the mode is not provided, the component will be hidden by default.
 * If the mode is "disable", the children of this component need to support a disabled prop.
 *
 * @example
 * ```tsx
 * <Policy policy="view-analytics" mode="disable">
 *     <AnalyticsSection />
 * </Policy>
 * ```
 *
 * This will disable the AnalyticsSection component if the user doesn't have the "view-analytics" policy.
 */
const Policy = ({ children, action, mode = "hide", communityId }) => {
    const hasAccess = usePolicyContext(action, communityId);
    if (hasAccess) {
        return React.createElement(React.Fragment, null, children);
    }
    if (mode === "hide") {
        return null;
    }
    if (mode === "block") {
        return (React.createElement(React.Fragment, null,
            React.createElement(PeekBox, { "data-testid": "blocked-feature", position: "absolute", top: 0, left: 0, width: "100%", height: "100%", sx: {
                    zIndex: 10,
                    "+ *": {
                        mixBlendMode: "luminosity",
                        opacity: 0.5,
                    },
                    ":hover": {
                        cursor: "not-allowed !important" ,
                    },
                } }),
            children));
    }
    const disabledElement = React.cloneElement(children, { disabled: true });
    return React.createElement(React.Fragment, null, disabledElement);
};
/**
 *
 * @param policies - an array of policies to be checked against the policies provided in <PolicyContextProvider />
 * @returns {boolean} - true if the user has all the policies, false otherwise
 */
const usePolicyContext = (action, communityId) => {
    const { allow, deny } = usePolicyContextAux();
    function checkAction(userAction) {
        if (communityId) {
            if (userAction.includes(action) && userAction.includes(communityId)) {
                return true;
            }
        }
        if ((allow === null || allow === void 0 ? void 0 : allow.includes(action)) && !(deny === null || deny === void 0 ? void 0 : deny.includes(action))) {
            return true;
        }
        return false;
    }
    if (communityId) {
        return !deny.some(checkAction) && allow.some(checkAction);
    }
    // For non-community checks, use direct includes instead of checkAction
    return (allow === null || allow === void 0 ? void 0 : allow.includes(action)) && !(deny === null || deny === void 0 ? void 0 : deny.includes(action));
};

export { Policy, PolicyContext, PolicyContextProvider, usePolicyContext, usePolicyContextAux };
